/* eslint-disable consistent-return */
/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable import/no-cycle */
import { AccordionSummary, AccordionDetails } from '@mui/material'
import MuiAccordion from '@mui/material/Accordion'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { useTranslation } from 'react-i18next'
import { primaryColors } from '@assets/styles/helpers/colors.styles'
import AccordionItemModal from '@/shared/components/Modal/AccordionItemModal/AccordionItemModal'
import ConfirmationModal from '@/shared/components/Modal/ConfirmationModal/ConfirmationModal'
import { accordionItemModel } from '@/features/menu-management/SidebarAccordion/api/SidebarAccordionAPI.model'
import { ChangeEvent, useEffect, useRef, useState } from 'react'
import TextField from '@/shared/components/TextField/TextField'
import IconButton from '@/shared/components/IconButton/IconButton'
import CompressOutlinedIcon from '@mui/icons-material/CompressOutlined'
import { TranslationItemModel } from '@/shared/api/BaseAPI.model'
import styles from './AccordionContent.module.scss'
import {
  accordionDetailsStyles,
  accordionstyles
} from './AccordionContent.styles'
import TranslationItem from '../TranslationItem/TranslationItem'
import AccordionTileItem from '../AccordionTileItem/AccordionTileItem'
import { useSelectedLanguagesQuery } from '../../hooks/useSelectedLanguagesQuery'

interface AccordionContentProps {
  title: string
  modalTitle: string
  accordionIndex: number
  openAccordion: number
  handleAccordionClick: (index: number) => void
  contentList: accordionItemModel[]
  deleteFunction: (id: string) => void
  /**
   * What the delete confirmation says happens to the dishes that use this item.
   * Per accordion, because the answer differs: a category or a menu type only
   * loses its assignment, while a size takes every dish's price for it along.
   */
  deleteMessage: string
  /**
   * The row whose record is being fetched for the edit form, if any. Only the
   * accordion knows it — the GET lives in its own query hook.
   */
  loadingItemId?: string
  accordionItemFormConfig: any
  handleEditAccordionItem: (id: string) => void
  handleAddAccordionItem: () => void
  handleOnCloseModal: () => void
  isModalOpen: boolean
}

function AccordionContent(props: AccordionContentProps) {
  const { t } = useTranslation()
  const {
    title,
    modalTitle,
    accordionIndex,
    openAccordion,
    handleAccordionClick,
    contentList,
    deleteFunction,
    deleteMessage,
    loadingItemId,
    accordionItemFormConfig,
    handleEditAccordionItem,
    handleAddAccordionItem,
    handleOnCloseModal,
    isModalOpen
  } = props

  /*
    The item the bin was clicked on, held until it is confirmed. The name comes
    along so the question can say which one it is — three rows can read "Zupy"
    and the list reorders itself under a search or a sort, so "are you sure"
    alone is not enough to know what is about to go.
  */
  const [pendingDelete, setPendingDelete] = useState<{
    id: string
    name: string
  } | null>(null)

  const [sortedAlphabetically, setSortedAlphabetically] = useState(false)

  const [expandedItems, setExpandedItems] = useState<Record<string, boolean>>(
    {}
  )
  const toggleItemExpanded = (id: string) => {
    setExpandedItems(prev => ({
      ...prev,
      [id]: !prev[id]
    }))
  }

  const [filteredList, setFilteredList] = useState<accordionItemModel[]>([])

  const containerRef = useRef<HTMLDivElement | null>(null)
  const [isScrollable, setIsScrollable] = useState(false)
  const currentLanguage =
    useTranslation().i18n.language || localStorage.getItem('i18nextLng') || ''
  const { defaultLanguage } = useSelectedLanguagesQuery(currentLanguage)

  const sortList = (list: accordionItemModel[], ascending: boolean) =>
    [...list].sort((a, b) => {
      const nameA =
        a.translations
          .find(
            translation =>
              translation.languageCode === defaultLanguage?.languageCode
          )
          ?.name.toLowerCase() || ''
      const nameB =
        b.translations
          .find(
            translation =>
              translation.languageCode === defaultLanguage?.languageCode
          )
          ?.name.toLowerCase() || ''
      return ascending ? nameA.localeCompare(nameB) : nameB.localeCompare(nameA)
    })

  const handleSort = (ascending: boolean) => {
    const listToSort = filteredList?.length ? filteredList : contentList
    const sortedList = sortList(listToSort || [], ascending)
    setSortedAlphabetically(ascending)
    setFilteredList(sortedList)
  }

  const filterList = (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    if (!event.target) return
    const { value } = event.target
    const searchTerm = value.toLowerCase()

    let filteredItems =
      contentList?.filter(item =>
        item.translations.some(
          translation =>
            translation.name !== t('SIDEBAR_ACCORDION.MISSING_TRANSLATION') &&
            translation.name.toLowerCase().includes(searchTerm)
        )
      ) || []

    if (sortedAlphabetically !== null) {
      filteredItems = sortList(filteredItems, sortedAlphabetically)
    }

    setFilteredList(filteredItems)
  }

  useEffect(() => {
    if (contentList) {
      setFilteredList(contentList)
    }
  }, [contentList])

  useEffect(() => {
    const container = containerRef.current
    if (!container) return

    const checkScroll = () => {
      setIsScrollable(container.scrollHeight > container.clientHeight)
    }

    checkScroll()

    const observer = new ResizeObserver(() => checkScroll())
    observer.observe(container)

    return () => {
      observer.disconnect()
    }
  }, [openAccordion])

  const isCollapseconfirmButtonDisabled =
    !Object.keys(expandedItems).length ||
    Object.keys(expandedItems).every(key => !expandedItems[key])

  return (
    <MuiAccordion
      expanded={openAccordion === accordionIndex}
      onChange={() => handleAccordionClick(accordionIndex)}
      sx={{
        ...accordionstyles,
        overflow: 'hidden'
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon sx={{ color: primaryColors.white }} />}
      >
        <p className={`${styles.accordionTitle} body-bold-medium`}>{title}</p>
      </AccordionSummary>
      <AccordionDetails sx={accordionDetailsStyles}>
        <>
          <TranslationItem
            languageCode={defaultLanguage?.languageCode || ''}
            translationText={t('SIDEBAR_ACCORDION.DEFAULT_LANGUAGE')}
            textAlignment="left"
            fontSize="body-medium"
          />
          <div className={styles.accordionFilterContainer}>
            <TextField
              label={title}
              placeholder="Search..."
              focused
              onChange={filterList}
            />
            {sortedAlphabetically ? (
              <IconButton
                icon="icon-big la-sort-alpha-up-alt"
                color="primary"
                onClick={() => handleSort(false)}
              />
            ) : (
              <IconButton
                icon="icon-big la-sort-alpha-down"
                color="primary"
                onClick={() => handleSort(true)}
              />
            )}
            <IconButton
              icon={<CompressOutlinedIcon />}
              color="primary"
              onClick={() => setExpandedItems({})}
              disabled={isCollapseconfirmButtonDisabled}
            />
            <IconButton
              icon={<i className="icon-big las la-plus" />}
              color="primary"
              onClick={handleAddAccordionItem}
            />
          </div>
        </>
        {contentList?.length > 0 ? (
          <div
            ref={containerRef}
            className={`${styles.accordionTilesContainer} ${
              isScrollable ? styles.scrollable : ''
            }`}
          >
            {filteredList?.map((item: accordionItemModel) => {
              const defaultTranslation = item.translations.find(
                translation =>
                  translation.languageCode === defaultLanguage?.languageCode
              )
              const translationList = item.translations.filter(
                translation =>
                  translation.languageCode !== defaultLanguage?.languageCode
              )
              const relatedItemsList = item.relatedItems?.map(
                (relatedItem: TranslationItemModel) => relatedItem.name
              )

              return (
                <AccordionTileItem
                  key={item.id}
                  title={defaultTranslation?.name || ''}
                  translations={translationList}
                  onDelete={() =>
                    setPendingDelete({
                      id: item.id!,
                      // A row with no name in the default language would put
                      // empty quotes in the question.
                      name:
                        defaultTranslation?.name ||
                        t('SIDEBAR_ACCORDION.MISSING_TRANSLATION')
                    })
                  }
                  onEdit={() => handleEditAccordionItem(item.id!)}
                  isEditLoading={!!loadingItemId && loadingItemId === item.id}
                  isExpanded={!!expandedItems[item.id!]}
                  onToggle={() => toggleItemExpanded(item.id!)}
                  relatedItemsNames={relatedItemsList}
                />
              )
            })}
          </div>
        ) : (
          <div className={styles.accordionNoDataContainer}>
            <p className={`${styles.accordionNoDataText} body-bold-medium`}>
              {t('SIDEBAR_ACCORDION.NO_DATA.FIRST_PART')}
            </p>
            <div className={styles.accordionNoDataDescriptionContainer}>
              <p className={`${styles.accordionNoDataDescription} body-medium`}>
                {t('SIDEBAR_ACCORDION.NO_DATA.SECOND_PART')}
              </p>
              <p className={`${styles.accordionNoDataItemName} body-medium`}>
                {modalTitle}
              </p>
              <p className={`${styles.accordionNoDataDescription} body-medium`}>
                {t('SIDEBAR_ACCORDION.NO_DATA.THIRD_PART')}
              </p>
            </div>
          </div>
        )}
      </AccordionDetails>
      <AccordionItemModal
        isOpen={isModalOpen}
        title={modalTitle}
        onClose={handleOnCloseModal}
        accordionItemFormConfig={accordionItemFormConfig}
      />
      {/*
        Deleting here cascades in the database, and nothing on this screen shows
        what depends on the row, so the question has to carry that itself.
      */}
      <ConfirmationModal
        isOpen={pendingDelete !== null}
        title={t('SIDEBAR_ACCORDION.DELETE.TITLE', {
          name: pendingDelete?.name ?? ''
        })}
        message={deleteMessage}
        onConfirm={() => {
          if (pendingDelete) deleteFunction(pendingDelete.id)
          setPendingDelete(null)
        }}
        onClose={() => setPendingDelete(null)}
        // The add/edit form may be open behind this; clearing the global dirty
        // flag would stop it warning when it is closed with unsaved changes.
        resetsFormDirty={false}
      />
    </MuiAccordion>
  )
}

export default AccordionContent
